// Dan's pricing rule (mirrors backend/planner/pricing.py): the target "margin" is a markup on
// cost, and the shelf price rounds UP to the next quarter. $1.00 at 65% -> $1.65 -> $1.75.
// The account default is tiered: items costing up to the tier ($2.50) take the low-price markup (100%),
// dearer ones the regular markup (65%) but never below what an item at the tier sells for. $1.00 minimum.
// A "margin" below is a number (one flat markup, e.g. a product override) or a rule object.
export const DEFAULT_MARGIN_PCT = 65;
export const DEFAULT_LOW_MARGIN_PCT = 100;
export const DEFAULT_TIER_COST = 2.5;
export const DEFAULT_MIN_PRICE = 1;

export const ruleFromSettings = (s) => ({
  high: Number(s?.target_margin_pct) || DEFAULT_MARGIN_PCT,
  low: s?.markup_low_pct == null ? DEFAULT_LOW_MARGIN_PCT : Number(s.markup_low_pct),
  tier: s?.markup_tier_cost == null ? DEFAULT_TIER_COST : Number(s.markup_tier_cost),
  min: s?.min_vend_price == null ? DEFAULT_MIN_PRICE : Number(s.min_vend_price),
});
export const DEFAULT_RULE = ruleFromSettings(null);

const isRule = (m) => m != null && typeof m === "object";
const quarterUp = (raw) => Math.ceil(raw * 4 - 1e-9) / 4; // epsilon: exact quarters stay put

// The markup % that applies to this cost.
export const markupFor = (cost, m) => {
  if (isRule(m)) return m.tier > 0 && Number(cost) <= m.tier ? m.low : m.high;
  return m === "" || m == null || Number.isNaN(Number(m)) || Number(m) < 0 ? DEFAULT_MARGIN_PCT : Number(m);
};

// "100% under $1.50, 65% above" for a rule; "65%" for a number.
export const markupLabel = (m) => (isRule(m)
  ? (m.tier > 0 ? `${m.low}% up to $${m.tier.toFixed(2)}, ${m.high}% above` : `${m.high}%`)
  : `${markupFor(null, m)}%`);

export const priceForMargin = (cost, marginPct = DEFAULT_RULE) => {
  const c = Number(cost);
  if (!c || c <= 0) return 0;
  const m = marginPct == null || marginPct === "" ? DEFAULT_RULE : marginPct;
  let price = quarterUp(c * (1 + markupFor(c, m) / 100));
  if (isRule(m) && m.tier > 0 && c > m.tier) price = Math.max(price, quarterUp(m.tier * (1 + m.low / 100)));
  return Math.max(price, isRule(m) ? (m.min ?? DEFAULT_MIN_PRICE) : DEFAULT_MIN_PRICE);
};

// Profit as a % of cost, one decimal.
export const marginOnCost = (cost, price) => {
  const c = Number(cost); const p = Number(price);
  if (cost == null || !c || c <= 0 || !p) return null;
  return Math.round(((p - c) / c) * 1000) / 10;
};

// Per-unit cost: case price / case pack when known, else the typed unit cost.
export const unitCost = (p) => {
  if (!p) return null;
  if (p.case_price != null && p.case_pack) return p.case_price / p.case_pack;
  return p.cost != null ? Number(p.cost) : null;
};

// Shelf price: the typed vend price, else the rule's price.
export const shelfPrice = (p, globalMargin) => {
  if (!p) return null;
  if (p.vend_price != null) return Number(p.vend_price);
  const uc = unitCost(p);
  return uc != null ? priceForMargin(uc, p.target_margin_pct ?? globalMargin) || null : null;
};

export const money = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : `$${Number(v).toFixed(2)}`);
